// src/components/scenes/SceneCard.js
import React, { useState, useContext, useMemo } from 'react';
import { Play, Loader2, Check, Trash2 } from 'lucide-react';
import { AppContext } from '../../context/AppContext';
import { toast } from '../../lib/toast';
import { Card, CardContent } from '../ui/card';
import { getSceneBackground } from '../../lib/sceneBackgrounds';
import { iconForCategory } from '../../lib/sceneCategories';

const SceneCard = ({ scene, devices, onEdit, onDetails }) => {
  const { activateScene, deleteScene } = useContext(AppContext);

  const [isRunning, setIsRunning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const sceneBackground = useMemo(() => {
    return getSceneBackground(scene.category || 'General');
  }, [scene.category]);

  // Icon comes from the category (scene.type is always "custom", so switching
  // on it made every scene show the same fallback icon).
  const Icon = useMemo(() => iconForCategory(scene.category), [scene.category]);

  const handleActivate = async (e) => {
    e?.stopPropagation?.();
    if (isRunning) return;

    try {
      setIsRunning(true);
      await activateScene(scene.id);

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 1500);
    } catch (error) {
      console.log('Error activating scene:', error);
      toast.error(`Failed to activate scene "${scene.name}"`);
    } finally {
      setTimeout(() => setIsRunning(false), 2000);
    }
  };

  const handleCardClick = (e) => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    onDetails?.(scene);
  };

  const handleMouseDown = () => {
    const timer = setTimeout(() => {
      setShowContextMenu(true);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setShowContextMenu(true);
  };

  const handleEdit = (e) => {
    e?.stopPropagation?.();
    setShowContextMenu(false);
    onEdit?.(scene);
  };

  const handleDelete = async (e) => {
    e?.stopPropagation?.();

    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    try {
      await deleteScene(scene.id);
      // toast.success(`Scene "${scene.name}" deleted successfully`);
      setShowContextMenu(false);
    } catch (error) {
      console.error('Error deleting scene:', error);
      toast.error(`Failed to delete scene "${scene.name}"`);
      setDeleteConfirm(false);
    }
  };

  const cancelDelete = (e) => {
    e?.stopPropagation?.();
    setDeleteConfirm(false);
  };

  return (
    <>
      <Card
        className={[
          'overflow-hidden rounded-2xl border border-white/20',
          'bg-gradient-to-br',
          sceneBackground.gradient,
          'transition-all duration-200',
          'hover:shadow-xl active:scale-[0.96]',
          showSuccess ? 'ring-2 ring-primary/50' : ''
        ].join(' ')}
        onClick={handleCardClick}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onContextMenu={handleContextMenu}
      >
        <CardContent className="p-4">
          <div className="flex flex-col gap-3">
            {/* Icon + Name */}
            <div className="flex items-start gap-3">
              <div className={[
                'h-10 w-10 rounded-2xl border border-white/20',
                'flex items-center justify-center flex-shrink-0',
                'backdrop-blur-sm',
                sceneBackground.iconBg
              ].join(' ')}>
                <Icon className={['h-5 w-5', sceneBackground.iconColor].join(' ')} />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold truncate text-slate-900">
                  {scene.name}
                </h3>
                <p className="text-xs text-slate-600/80 mt-0.5">
                  {scene.category || 'General'}
                </p>
              </div>
            </div>

            {/* Run Button */}
            <button
              type="button"
              onClick={handleActivate}
              disabled={isRunning}
              className={[
                'w-full h-10 rounded-xl flex items-center justify-center gap-2 text-sm font-medium',
                'transition-all duration-200',
                'backdrop-blur-sm border border-white/20',
                isRunning || showSuccess
                  ? 'bg-white/30 text-white shadow-lg'
                  : 'bg-white/20 hover:bg-white/30 text-slate-900'
              ].join(' ')}
            >
              {isRunning && !showSuccess ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : showSuccess ? (
                <>
                  <Check className="h-4 w-4" />
                  Done
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run
                </>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Context Menu (Long Press / Right Click) */}
      {showContextMenu && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => {
            setShowContextMenu(false);
            setDeleteConfirm(false);
          }}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-background border-t border-border rounded-t-2xl p-4 animate-in slide-in-from-bottom duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-border rounded-full mx-auto mb-4" />

            {!deleteConfirm ? (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={handleEdit}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors text-left"
                >
                  <Play className="h-5 w-5" />
                  <span className="text-sm font-medium">Edit Scene</span>
                </button>

                <button
                  type="button"
                  onClick={handleDelete}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-destructive/10 transition-colors text-left text-destructive"
                >
                  <Trash2 className="h-5 w-5" />
                  <span className="text-sm font-medium">Delete Scene</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="px-2">
                  <p className="text-xs text-muted-foreground">
                    Delete this scene? This can't be undone.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors text-sm font-medium"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={cancelDelete}
                    className="flex items-center justify-center gap-2 p-3 rounded-xl bg-muted hover:bg-muted/80 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SceneCard;
